import { useEffect, useMemo, useState } from "react";
import styles from "./containerList.module.css";
import {
  TextField,
  Button,
  Table,
  TableCell,
  TableHead,
  TableContainer,
  Paper,
  TableBody,
  TableRow,
  TableFooter,
  Dialog,
  Snackbar,
  Alert,
  Switch,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { StyledMainTableCell, StyledMainTableRow } from "../../components/TableComponent";
import TablePaginationComponent from "../../components/TablePagination";
import { Add, SearchOutlined, ClearRounded, Inventory2Outlined } from "@mui/icons-material";
import axios from "axios";
import DeletePopup from "../../components/DeletePopup";
import Loader from "../../components/Loader";
import ContainerListLoader from "./ContainerListLoader";
import { authData, withAdminAuth } from "../../components/GetAuth";
import { Edit, Trash2 } from "react-feather";
import PageHeader from "../../components/PageHeader";
import { useWebsite } from "../../../context/WebsiteContext";
import { useSiteBranding } from "../../../hooks/useSiteBranding";
import AddContainerForm from "../../components/popups/AddContainerForm";

const ContainerList = () => {
  const auth = authData();
  const API_TOKEN = auth?.api_token;
  const [loading, setLoading] = useState(false);
  const [contentLoading, setContentLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [allData, setAllData] = useState([]);
  const [searchKey, setSearchKey] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarType, setSnackbarType] = useState("success");
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(0);
  const [editData, setEditData] = useState(null);

  const { websiteData } = useWebsite();
  useSiteBranding("Container List", websiteData);

  const canUpdate = auth?.user_type === 2 || auth?.permissions?.indexOf("shipping:update-container") > -1;
  const canCreate = auth?.user_type === 2 || auth?.permissions?.indexOf("shipping:create-container") > -1;

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const fetchContainers = async () => {
    setContentLoading(true);

    try {
      const { data } = await axios.get(`${process.env.REACT_APP_API_URL}v1/containers`, {
        headers: {
          Authorization: `Bearer ${API_TOKEN}`,
        },
      });

      setAllData(data?.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setContentLoading(false);
    }
  };

  useEffect(() => {
    fetchContainers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API_TOKEN]);

  const filteredData = useMemo(() => {
    if (!searchKey.trim()) return allData;

    const key = searchKey.toLowerCase();

    return allData.filter((item) => {
      return (
        item?.seal_ref_no?.toLowerCase().includes(key) ||
        item?.container_no?.toLowerCase().includes(key) ||
        item?.freight?.toLowerCase().includes(key) ||
        item?.address?.toLowerCase().includes(key) ||
        item?.contact_no?.toLowerCase().includes(key)
      );
    });
  }, [allData, searchKey]);

  const handleClearSearch = () => {
    setSearchKey("");
    setPage(0);
  };

  const pagedData = filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const deleteConfirm = () => {
    setLoading(true);
    setDeleteModalOpen(false);
    axios
      .delete(`${process.env.REACT_APP_API_URL}v1/containers/${deleteId}`, {
        headers: {
          Authorization: `Bearer ${API_TOKEN}`,
        },
      })
      .then((response) => {
        setLoading(false);
        if (response.data.status === "SUCCESS") {
          setSnackbarOpen(true);
          setSnackbarMessage(response.data.message);
          setSnackbarType("success");
          setAllData((prev) => prev.filter((i) => i.id !== deleteId));
          setDeleteId(0);
        }
        if (response.data.status === "ERROR") {
          setSnackbarOpen(true);
          setSnackbarMessage(response.data.message);
          setSnackbarType("error");
        }
      })
      .catch((error) => {
        setLoading(false);
        if (error?.response?.data?.messages) {
          setSnackbarOpen(true);
          setSnackbarMessage(error.response.data.messages);
          setSnackbarType("error");
        }
      });
  };

  const statusChange = (e, item) => {
    const status = e.target.checked ? 1 : 0;
    setLoading(true);
    axios
      .put(
        `${process.env.REACT_APP_API_URL}v1/containers/${item?.id}`,
        { status },
        {
          headers: {
            Authorization: `Bearer ${API_TOKEN}`,
          },
        }
      )
      .then((response) => {
        setLoading(false);
        if (response.data.status === "SUCCESS") {
          setSnackbarOpen(true);
          setSnackbarMessage(response.data.message);
          setSnackbarType("success");
          setAllData((prev) => prev.map((i) => (i.id === item?.id ? { ...item, status } : i)));
        }
        if (response.data.status === "ERROR") {
          setSnackbarOpen(true);
          setSnackbarMessage(response.data.message);
          setSnackbarType("error");
        }
      })
      .catch((error) => {
        setLoading(false);
        if (error?.response?.data?.messages) {
          setSnackbarOpen(true);
          setSnackbarMessage(error.response.data.messages);
          setSnackbarType("error");
        }
      });
  };

  return (
    <>
      <Loader open={loading} setOpen={setLoading} />

      <div className={styles.MainDiv}>
        <PageHeader title="Container" trail={[{ label: "List" }]} />

        <div className={styles.MainCard}>
          {contentLoading && <ContainerListLoader />}

          {!contentLoading && (
            <>
              <div className={styles.FilterBar}>
                <div className={styles.SearchBox}>
                  <TextField
                    fullWidth
                    placeholder="Search reference, container, forwarder, address or contact..."
                    value={searchKey}
                    onChange={(e) => {
                      setSearchKey(e.target.value);
                      setPage(0);
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchOutlined className={styles.SearchIcon} />
                        </InputAdornment>
                      ),
                      endAdornment: searchKey && (
                        <InputAdornment position="end">
                          <IconButton size="small" aria-label="Clear search" onClick={handleClearSearch}>
                            <ClearRounded fontSize="small" className={styles.ClearButton} />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  {searchKey && (
                    <span className={styles.ResultCount}>
                      {filteredData.length} {filteredData.length === 1 ? "result" : "results"}
                    </span>
                  )}
                </div>

                {canCreate && (
                  <Button
                    className={styles.AddButton}
                    onClick={() => {
                      setFormOpen(true);
                      setEditData(null);
                    }}
                  >
                    <Add /> Container
                  </Button>
                )}
              </div>

              {/* Desktop table */}
              <div className={`${styles.TableCard} ${styles.DesktopOnly}`}>
                <TableContainer component={Paper}>
                  <Table aria-label="Container list table">
                    <TableHead>
                      <TableRow>
                        <StyledMainTableCell>Reference Id</StyledMainTableCell>
                        <StyledMainTableCell>Container Number</StyledMainTableCell>
                        <StyledMainTableCell>Freight Forwarder</StyledMainTableCell>
                        <StyledMainTableCell>Container Address</StyledMainTableCell>
                        <StyledMainTableCell>Contact Person Number</StyledMainTableCell>
                        <StyledMainTableCell>Active</StyledMainTableCell>
                        {canUpdate && <StyledMainTableCell align="right">Actions</StyledMainTableCell>}
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {pagedData.length > 0 ? (
                        pagedData.map((item) => (
                          <StyledMainTableRow key={item.id} hover>
                            <TableCell>
                              <strong>{item?.seal_ref_no}</strong>
                            </TableCell>
                            <TableCell>{item?.container_no}</TableCell>
                            <TableCell>{item?.freight}</TableCell>
                            <TableCell>{item?.address}</TableCell>
                            <TableCell>{item?.contact_no}</TableCell>
                            <TableCell>
                              <Switch
                                checked={!!item?.status}
                                onChange={(e) => statusChange(e, item)}
                                inputProps={{ "aria-label": "Toggle container active status" }}
                                disabled={!canUpdate}
                                className={styles.CustomSwitch}
                              />
                            </TableCell>
                            {canUpdate && (
                              <TableCell align="right">
                                <IconButton
                                  aria-label="Edit container"
                                  className={`${styles.ActionBtn} ${styles.EditBtn}`}
                                  onClick={() => {
                                    setFormOpen(true);
                                    setEditData(item);
                                  }}
                                >
                                  <Edit />
                                </IconButton>
                                <IconButton
                                  aria-label="Delete container"
                                  className={`${styles.ActionBtn} ${styles.DeleteBtn}`}
                                  onClick={() => {
                                    setDeleteModalOpen(true);
                                    setDeleteId(item?.id);
                                  }}
                                >
                                  <Trash2 />
                                </IconButton>
                              </TableCell>
                            )}
                          </StyledMainTableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell align="center" colSpan={canUpdate ? 7 : 6} className={styles.EmptyState}>
                            <Inventory2Outlined sx={{ fontSize: 60, opacity: 0.25, marginBottom: 1 }} />
                            <h3>No containers found</h3>
                            <p>No records matched your search.</p>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>

                    {filteredData.length > 0 && (
                      <TableFooter>
                        <TableRow>
                          <TablePaginationComponent
                            count={filteredData.length}
                            colSpan={canUpdate ? 7 : 6}
                            page={page}
                            rowsPerPage={rowsPerPage}
                            handleChangePage={handleChangePage}
                            handleChangeRowsPerPage={handleRowsPerPage}
                          />
                        </TableRow>
                      </TableFooter>
                    )}
                  </Table>
                </TableContainer>
              </div>

              {/* Mobile card list */}
              <div className={styles.MobileOnly}>
                {filteredData.length === 0 && (
                  <div className={styles.CardEmpty}>
                    <Inventory2Outlined sx={{ fontSize: 44, opacity: 0.25, marginBottom: 1 }} />
                    <h3>No containers found</h3>
                    <p>No records matched your search.</p>
                  </div>
                )}

                {filteredData.length > 0 && (
                  <>
                    <div className={styles.CardList}>
                      {pagedData.map((item) => (
                        <div className={styles.CardItem} key={item.id}>
                          <div className={styles.CardTopRow}>
                            <div>
                              <div className={styles.CardContainerNo}>{item?.container_no}</div>
                              <div className={styles.CardRefNo}>Ref: {item?.seal_ref_no}</div>
                            </div>
                            <Switch
                              checked={!!item?.status}
                              onChange={(e) => statusChange(e, item)}
                              inputProps={{ "aria-label": "Toggle container active status" }}
                              disabled={!canUpdate}
                              className={styles.CustomSwitch}
                            />
                          </div>

                          <div className={styles.CardFieldRow}>
                            <span className={styles.CardFieldLabel}>Freight Forwarder</span>
                            <span className={styles.CardFieldValue}>{item?.freight || "—"}</span>
                          </div>
                          <div className={styles.CardFieldRow}>
                            <span className={styles.CardFieldLabel}>Address</span>
                            <span className={styles.CardFieldValue}>{item?.address || "—"}</span>
                          </div>
                          <div className={styles.CardFieldRow}>
                            <span className={styles.CardFieldLabel}>Contact Number</span>
                            <span className={styles.CardFieldValue}>{item?.contact_no || "—"}</span>
                          </div>

                          {canUpdate && (
                            <div className={styles.CardActionsRow}>
                              <IconButton
                                aria-label="Edit container"
                                className={`${styles.ActionBtn} ${styles.EditBtn}`}
                                onClick={() => {
                                  setFormOpen(true);
                                  setEditData(item);
                                }}
                              >
                                <Edit />
                              </IconButton>
                              <IconButton
                                aria-label="Delete container"
                                className={`${styles.ActionBtn} ${styles.DeleteBtn}`}
                                onClick={() => {
                                  setDeleteModalOpen(true);
                                  setDeleteId(item?.id);
                                }}
                              >
                                <Trash2 />
                              </IconButton>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>

                    <TablePaginationComponent
                      count={filteredData.length}
                      colSpan={1}
                      page={page}
                      rowsPerPage={rowsPerPage}
                      handleChangePage={handleChangePage}
                      handleChangeRowsPerPage={handleRowsPerPage}
                      component="div"
                    />
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      <Dialog open={formOpen} onClose={() => setFormOpen(false)} fullWidth maxWidth="sm">
        <AddContainerForm
          setFormOpen={setFormOpen}
          setAllData={setAllData}
          setSnackbarOpen={setSnackbarOpen}
          setSnackbarMessage={setSnackbarMessage}
          setSnackbarType={setSnackbarType}
          setLoading={setLoading}
          editData={editData}
        />
      </Dialog>

      <DeletePopup open={deleteModalOpen} setOpen={setDeleteModalOpen} confirmAction={deleteConfirm} />

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarType} variant="filled" sx={{ width: "100%" }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default withAdminAuth(ContainerList);